/* eslint-disable global-require */
import MiniSearch, { type SearchResult } from 'minisearch';
import {
  ISearchResult, ISearchResultOther, ISearchResultRegion, ISearchResultResort, SearchCategory,
} from '@/api/home/types';
import { IRegionShort } from '@/api/region/types';
import { IResortPathInfo } from '@/api/resort/types';
import { SearchAltNames } from '@/api/type';
import { getLocale } from '@/i18n/locale';
import { IS_SSR } from '@/ots-constants';
import { Diagnostics } from '@/util/diagnostics';
import { I18nKey } from '@/i18n/types';
import { Routes } from '@/routes';

// MiniSearch replaces the previous three-Fuse-instance setup. The old
// code ran one Fuse instance per category (resorts / regions / other),
// then concatenated by category — no cross-type relevance, weak prefix
// affinity, and equal weight on every field. MiniSearch gives us:
//   - BM25 scoring (titles outrank slug/alt by default)
//   - native prefix matching → "vail" -> "Vail Mountain" ranks high
//   - native fuzzy (edit distance) for typos
//   - per-field `boost: { title: 3, alt: 0.5 }` weighting
//   - a single sorted result list (we split back into categories
//     below for backwards compatibility with existing callers)
//
// The static misspellings JSON is still loaded and merged into each
// resort's `alt` field for safety on the first cutover; once we trust
// MiniSearch's `fuzzy` setting we can drop that 191 KB asset.
//
// The `?t=` query-string threshold escape hatch from the Fuse era is
// retained — same URL contract works for ad-hoc tuning of `fuzzy`.

const overrideFuzzy = (!IS_SSR && window.location.search && window.location.search.startsWith('?t='))
  ? (+window.location.search.substr(3))
  : undefined;

Diagnostics.debug(`[search] using fuzzy override: ${overrideFuzzy ?? 'NO. Using default 0.2'}`);

// Document shape inside MiniSearch. We tag each doc with `kind` so we
// can split results back into the per-category buckets the existing
// API surface promises.
type SearchKind = 'resort' | 'region' | 'other';
type IOtherHref = { uuid: string, title: string, href: string };

interface IndexedDoc {
  id: string; // composite to avoid uuid collisions across kinds
  kind: SearchKind;
  // Searchable fields:
  title: string;
  title_short?: string;
  title_original?: string;
  slug?: string;
  alt?: string;
  country?: string;
  state?: string;
  // Stored payload (returned with each hit):
  resort?: IResortPathInfo & { alt?: string[] };
  region?: IRegionShort;
  href?: string;
}

const SEARCH_FIELDS = ['title', 'title_short', 'title_original', 'slug', 'alt', 'country', 'state'];
const STORE_FIELDS = ['kind', 'title', 'resort', 'region', 'href'];

const BOOST = {
  title: 3,
  title_short: 2.5,
  title_original: 2.5,
  slug: 0.8,
  alt: 0.5, // alt holds misspellings + nicknames; intentionally weak
  country: 1,
  state: 1,
};

const loadFile = async <T>(file: string): Promise<T> => {
  const res = await fetch(file);
  return res.json() as Promise<T>;
};

const loadResorts = () => loadFile<Array<IResortPathInfo & { alt?: string[] }>>(`/index/resorts-${getLocale()}.json`);
const loadResortAlts = () => loadFile<Array<SearchAltNames>>(`/index/resorts-alt-${getLocale()}.json`);
const loadResortMisses = () => loadFile<Array<SearchAltNames>>(`/index/resorts-misspellings-${getLocale()}.json`);
const loadRegions = () => loadFile<{ regions: Array<IRegionShort & { alt?: string[] }> }>(`/index/regions-${getLocale()}.json`);

let indexPromise: Promise<MiniSearch<IndexedDoc>> | undefined;
let regionsMap: Map<string, IRegionShort> | undefined;

const loadOtherSearches = (t: (key: I18nKey['common']) => string): IOtherHref[] => [{
  uuid: 'homepage.nearbyResorts',
  title: t('homePage.nearbyResorts'),
  href: Routes.nearbyResorts(),
}];

const buildIndex = async (t: (key: I18nKey['common']) => string): Promise<MiniSearch<IndexedDoc>> => {
  const [resorts, resortAlts, resortMisses, regionsResp] = await Promise.all([
    loadResorts(),
    loadResortAlts(),
    loadResortMisses(),
    loadRegions(),
  ]);

  // Merge static alt + misspelling lists into each resort's `alt` field.
  // (Loose-equality match between resort.uuid and alts.uuid is intentional
  // — uuids are strings in JSON but the alts file occasionally serializes
  // them numerically.)
  [...resortAlts, ...resortMisses].forEach((entry) => {
    // eslint-disable-next-line eqeqeq
    const r = resorts.find((x) => x.uuid == entry.uuid);
    if (r && Array.isArray(entry.data)) {
      r.alt = [...(r.alt || []), ...entry.data];
    }
  });

  regionsMap = new Map(regionsResp.regions.map((r) => [r.uuid, r]));

  const docs: IndexedDoc[] = [
    ...resorts.map<IndexedDoc>((r) => ({
      id: `r:${r.uuid}`,
      kind: 'resort',
      title: r.title,
      title_short: r.title_short,
      title_original: r.title_original,
      slug: r.slug,
      alt: r.alt?.join(' '),
      resort: r,
    })),
    ...regionsResp.regions.map<IndexedDoc>((g) => ({
      id: `g:${g.uuid}`,
      kind: 'region',
      title: g.title,
      slug: g.slug,
      alt: g.alt?.join(' '),
      country: g.country,
      state: g.state,
      region: g,
    })),
    ...loadOtherSearches(t).map<IndexedDoc>((o) => ({
      id: `o:${o.uuid}`,
      kind: 'other',
      title: o.title,
      href: o.href,
    })),
  ];

  const mini = new MiniSearch<IndexedDoc>({
    fields: SEARCH_FIELDS,
    storeFields: STORE_FIELDS,
    // Treat hyphenated tokens as one bag (so "park-city" matches "Park City").
    tokenize: (text) => text.toLowerCase().split(/[\s\-,]+/).filter(Boolean),
    processTerm: (term) => term.toLowerCase(),
  });
  mini.addAll(docs);
  return mini;
};

export const fuseSearch = async (
  query: string,
  limit: number = 10,
  t: (key: I18nKey['common']) => string,
): Promise<ISearchResult> => {
  if (!indexPromise) {
    indexPromise = buildIndex(t);
  }
  const mini = await indexPromise;

  // Resort-over-region bias: users type resort names far more often
  // than region names, but BM25 by itself favors short docs like
  // regions (e.g. "Vail Valley" outranks the resort "Vail" because
  // the resort's title + title_short + slug + alt give it a longer
  // total token bag). 1.5x on resort docs offsets that.
  const RESORT_BOOST = 1.5;

  const rawHits = mini.search(query, {
    boost: BOOST,
    prefix: true,
    fuzzy: overrideFuzzy ?? 0.2,
    combineWith: 'AND',
    boostDocument: (_id, _term, storedFields) => (
      storedFields?.kind === 'resort' ? RESORT_BOOST : 1
    ),
  }) as Array<SearchResult & Partial<IndexedDoc>>;

  // Bubble exact-title matches to the very top of the list. A user
  // typing the full name of something should always see that thing
  // first, regardless of BM25 / boost / fuzzy quirks.
  const normalized = query.trim().toLowerCase();
  const isExactTitle = (h: SearchResult & Partial<IndexedDoc>) => (
    h.title?.toLowerCase().trim() === normalized
  );
  const hits = [
    ...rawHits.filter(isExactTitle),
    ...rawHits.filter((h) => !isExactTitle(h)),
  ];

  // Split MiniSearch's single sorted list back into the per-category
  // buckets the existing callers expect. Within each bucket, ordering
  // is preserved (it's MiniSearch's BM25 score order), so each list is
  // already best-first.
  const resorts: Array<ISearchResultResort> = [];
  const regions: Array<ISearchResultRegion> = [];
  const others: Array<ISearchResultOther> = [];

  hits.forEach((hit) => {
    if (hit.kind === 'resort' && hit.resort) {
      resorts.push({
        uuid: hit.resort.uuid,
        title: hit.resort.title,
        categoryUuid: SearchCategory.resorts,
        resort: hit.resort,
        regionTitle: regionsMap?.get(hit.resort.region_id)?.title,
        score: hit.score,
      });
    } else if (hit.kind === 'region' && hit.region) {
      regions.push({
        uuid: hit.region.uuid,
        title: hit.region.title,
        categoryUuid: SearchCategory.regions,
        region: hit.region,
        score: hit.score,
      });
    } else if (hit.kind === 'other' && hit.href) {
      others.push({
        uuid: hit.id.replace(/^o:/, ''),
        title: hit.title!,
        href: hit.href,
        categoryUuid: SearchCategory.other,
        score: hit.score,
      });
    }
  });

  return {
    categories: [
      { title: '', uuid: SearchCategory.other },
      { title: '', uuid: SearchCategory.regions },
      { title: '', uuid: SearchCategory.resorts },
    ],
    results: {
      other: others.slice(0, limit),
      resorts: resorts.slice(0, limit),
      regions: regions.slice(0, limit),
    },
    pagination: {
      count: resorts.length + regions.length,
      limit: resorts.length + regions.length,
      page: 0,
    },
  };
};
